import React, {useEffect, useState} from "react";
import {Button, Card, Checkbox, Col, Divider, Form, InputNumber, Row, Typography} from "antd";
import {useRequiredStore} from "../../../../../utils/store";
import {AgentDataStoreContext} from "../../agent.data.store";
import {useTranslate} from "../../../../../hooks/translate.hook";
import {ComponentPropsFromRoute} from "../../../../../routers/routers";
import {accountService} from "../../../../../services/accounts/account.service";
import {notifications} from "../../../../../services/notifications/notifications";
import {WarehouseWithSort} from "../../../../../models/warehouses/warehouse";
import {UIInputWidgetWarehouse} from "../../../../common/ui/input/widget/warehouse/ui.input.widget.warehouse";
import {Explanation} from "../../../../common/explanation/explanation";
import {RootStoreContext} from "../../../../../stores/root/root.store";
import {isAllowed} from "../../../../../utils/helpers";
import {
    WholesaleAccountWarehouseAgreement,
} from "../../../../../models/accounts/customer-account/wholesale-account/wholesale.account";
import {toData} from "../../../../common/ui/input/widget/warehouses-agreements/common/utils";
import {
    UiInputWidgetWarehousesAgreements,
} from "../../../../common/ui/input/widget/warehouses-agreements/ui.input.widget.warehouses-agreements";
import {AgreementData} from "../../../../common/ui/input/widget/warehouses-agreements/common/model";

export const AgentDataWarehouses: React.FC<ComponentPropsFromRoute> = () => {
    const {account, loadData} = useRequiredStore(AgentDataStoreContext);
    const {authStore: {attributes}} = useRequiredStore(RootStoreContext);
    const t = useTranslate();
    const [loading, setLoading] = useState<boolean>(true);
    const [warehouses, setWarehouses] = useState<WholesaleAccountWarehouseAgreement[]>([]);
    const [form] = Form.useForm();

    useEffect(() => {
        accountService.getWarehousesAgreements(account!.id).then((values) => {
            setWarehouses(values);
            setLoading(false);
        });
    }, [account]);

    if (loading || !account) {
        return <></>;
    }

    const submit = async (values: any) => {
        setLoading(true);
        try {
            await accountService.updateWarehouses(account.id, values);
            await loadData();
            notifications.successfully();
        } catch (e: any) {
            if (e.response?.data.fields) {
                form.setFields(e.response.data.fields);
            }
        } finally {
            setLoading(false);
        }
    };

    const defaultValues = {
        defaultWarehouse: account.defaultWarehouse?.id,
        warehouses: toData(warehouses),
        inTransit: {
            enabled: account.inTransitEnabled,
            maxQty: account.inTransitMaxQty,
        },
        backorder: {
            enabled: account.backorderWarehouseEnabled,
            ttl: account.backorderWarehouseOrderTtl,
        },
    };

    const inTransitAllowed = isAllowed(attributes, "order_create_reservation");
    const backorderAllowed = isAllowed(attributes, "order_create_backorder");
    const hasAdditionalSettings = inTransitAllowed || backorderAllowed;

    return (
        <Form
            size="middle"
            form={form}
            layout="vertical"
            name="client_form"
            initialValues={defaultValues}
            onFinish={async (values) => {
                await form.validateFields();
                values.warehouses.forEach((warehouse: WarehouseWithSort, index: number) => {
                    warehouse.sort = index;
                });
                await submit(values);
            }}
        >
            <Typography.Title level={3}>
                {t("CLIENT.WAREHOUSES.TITLE")}
            </Typography.Title>

            <Row gutter={[16, 16]} align="top">
                <Col xs={24} lg={hasAdditionalSettings ? 16 : 24}>
                    <Card
                        size="small"
                        title={t("CLIENT.WAREHOUSES.MAIN_SETTINGS", {
                            defaultValue: "Warehouse settings",
                        })}
                        style={{width: "100%"}}
                    >
                        <Form.Item name="warehouses">
                            <UiInputWidgetWarehousesAgreements />
                        </Form.Item>

                        <Form.Item noStyle shouldUpdate>
                            {() => (
                                <Form.Item
                                    name="defaultWarehouse"
                                    label={
                                        <Explanation
                                            title={t("ACCOUNT.SETTINGS.WAREHOUSE.DEFAULT.TITLE")}
                                            tooltip={t("ACCOUNT.SETTINGS.WAREHOUSE.DEFAULT.EXPL")}
                                        />
                                    }
                                    rules={[
                                        {
                                            required: true,
                                            message: t("FROM.ERROR.PLEASE_ENTER_VALUE"),
                                        },
                                    ]}
                                >
                                    <UIInputWidgetWarehouse
                                        allowedOnly={(form.getFieldValue("warehouses") || [])
                                            .map((warehouse: AgreementData) => warehouse.warehouse)}
                                    />
                                </Form.Item>
                            )}
                        </Form.Item>
                    </Card>
                </Col>

                {hasAdditionalSettings ? (
                    <Col xs={24} lg={8}>
                        <Card
                            size="small"
                            title={t("CLIENT.WAREHOUSES.ADDITIONAL_SETTINGS", {
                                defaultValue: "Additional settings",
                            })}
                            style={{width: "100%"}}
                        >
                            {inTransitAllowed ? (
                                <>
                                    <Divider orientation="left" plain style={{marginTop: 0}}>
                                        <Explanation
                                            title={t("CLIENT.WAREHOUSES.IN_TRANSIT.TITLE")}
                                            tooltip={t("CLIENT.WAREHOUSES.IN_TRANSIT.EXPL")}
                                        />
                                    </Divider>
                                    <Form.Item name={["inTransit", "enabled"]} valuePropName="checked">
                                        <Checkbox>{t("ENABLED")}</Checkbox>
                                    </Form.Item>
                                    <Form.Item
                                        name={["inTransit", "maxQty"]}
                                        label={t("MAX_AMOUNT")}
                                        rules={[
                                            {
                                                required: true,
                                                message: t("FROM.ERROR.PLEASE_ENTER_VALUE"),
                                            },
                                        ]}
                                    >
                                        <InputNumber
                                            disabled={loading}
                                            placeholder={`${t("EXAMPLE")}: 100`}
                                            style={{width: "100%"}}
                                        />
                                    </Form.Item>
                                </>
                            ) : null}

                            {backorderAllowed ? (
                                <>
                                    <Divider
                                        orientation="left"
                                        plain
                                        style={{marginTop: inTransitAllowed ? undefined : 0}}
                                    >
                                        <Explanation
                                            title={t("CLIENT.WAREHOUSES.BACKORDER.TITLE")}
                                            tooltip={t("CLIENT.WAREHOUSES.BACKORDER.EXPL")}
                                        />
                                    </Divider>
                                    <Form.Item name={["backorder", "enabled"]} valuePropName="checked">
                                        <Checkbox>{t("ENABLED")}</Checkbox>
                                    </Form.Item>
                                    <Form.Item
                                        name={["backorder", "ttl"]}
                                        label={t("CLIENT.WAREHOUSES.BACKORDER.TTL")}
                                        rules={[
                                            {
                                                required: true,
                                                message: t("FROM.ERROR.PLEASE_ENTER_VALUE"),
                                            },
                                        ]}
                                    >
                                        <InputNumber
                                            disabled={loading}
                                            placeholder={`${t("EXAMPLE")}: 100`}
                                            style={{width: "100%"}}
                                        />
                                    </Form.Item>
                                </>
                            ) : null}
                        </Card>
                    </Col>
                ) : null}
            </Row>

            <Row justify="end" style={{marginTop: 16}}>
                <Col>
                    <Button type="primary" onClick={() => form.submit()}>
                        {t("SAVE")}
                    </Button>
                </Col>
            </Row>
        </Form>
    );
};
